import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const EMPTY = {
  sku: "", name: "", description: "", category: "arreglo",
  flower_type_id: "", supplier_id: "",
  cost_price: 0, sale_price: 0, iva_rate: 0.15, min_stock: 0,
  unit: "unidad", image_url: "", active: true,
};

const CATS = [
  { v: "flor", l: "Flor" }, { v: "arreglo", l: "Arreglo" },
  { v: "ramo", l: "Ramo" }, { v: "planta", l: "Planta" },
  { v: "accesorio", l: "Accesorio" },
];

export default function Products() {
  const [items, setItems] = useState([]);
  const [flowerTypes, setFlowerTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/products").then((r) => setItems(r.data));

  useEffect(() => {
    load();
    api.get("/flower-types").then((r) => setFlowerTypes(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        cost_price: Number(form.cost_price) || 0,
        sale_price: Number(form.sale_price) || 0,
        iva_rate: Number(form.iva_rate) || 0,
        min_stock: Number(form.min_stock) || 0,
        flower_type_id: form.flower_type_id || null,
        supplier_id: form.supplier_id || null,
      };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      setForm(EMPTY); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onEdit = (p) => { setForm({ ...EMPTY, ...p, flower_type_id: p.flower_type_id || "", supplier_id: p.supplier_id || "" }); setEditing(p.id); setShowForm(true); };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar producto?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="products-page">
      <PageHeader
        title="Productos"
        subtitle="Arreglos florales, ramos, plantas y accesorios."
        testid="products-header"
        action={
          <button data-testid="products-create-button"
            onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo producto
          </button>
        }
      />

      {showForm && (
        <div data-testid="product-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} producto</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="SKU *"><input data-testid="product-sku" required className="input-soft" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></Field>
            <div className="md:col-span-2"><Field label="Nombre *"><input data-testid="product-name" required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field></div>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Categoría</span>
              <select className="input-soft" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Tipo de flor</span>
              <select className="input-soft" value={form.flower_type_id || ""} onChange={(e) => setForm({ ...form, flower_type_id: e.target.value })}>
                <option value="">— Ninguno —</option>
                {flowerTypes.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Proveedor</span>
              <select className="input-soft" value={form.supplier_id || ""} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                <option value="">— Ninguno —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.business_name}</option>)}
              </select>
            </label>
            <Field label="Costo"><input type="number" step="0.01" className="input-soft" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} /></Field>
            <Field label="Precio venta (sin IVA) *"><input data-testid="product-sale-price" type="number" step="0.01" required className="input-soft" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} /></Field>
            <Field label="IVA (%)">
              <select className="input-soft" value={form.iva_rate} onChange={(e) => setForm({ ...form, iva_rate: Number(e.target.value) })}>
                <option value={0.15}>15%</option>
                <option value={0}>0%</option>
              </select>
            </Field>
            <Field label="Stock mínimo"><input type="number" step="0.01" className="input-soft" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} /></Field>
            <Field label="Unidad"><input className="input-soft" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></Field>
            <Field label="URL imagen"><input className="input-soft" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></Field>
            <div className="md:col-span-3">
              <Field label="Descripción"><textarea rows={2} className="input-soft" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-3 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="product-save-button" className="btn-primary" type="submit">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin productos" hint="Registra tu primer producto." testid="products-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">SKU</th><th className="px-5 py-3">Producto</th>
                <th className="px-5 py-3">Categoría</th><th className="px-5 py-3">Precio</th>
                <th className="px-5 py-3">IVA</th><th className="px-5 py-3">Stock</th>
                <th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-4 font-mono">{p.sku}</td>
                  <td className="px-5 py-4 font-medium">{p.name}</td>
                  <td className="px-5 py-4 capitalize text-[#5C6B64]">{p.category}</td>
                  <td className="px-5 py-4">$ {Number(p.sale_price || 0).toFixed(2)}</td>
                  <td className="px-5 py-4">{(Number(p.iva_rate || 0) * 100).toFixed(0)}%</td>
                  <td className="px-5 py-4">
                    <span className="font-semibold">{Number(p.stock || 0).toFixed(2)}</span>
                    {Number(p.stock || 0) <= Number(p.min_stock || 0) && (
                      <span className="inline-flex ml-2 items-center gap-1 text-[#A44A4A] text-xs">
                        <AlertTriangle className="w-3 h-3" /> bajo
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => onEdit(p)} className="p-2 rounded hover:bg-[#F3F2ED]"><Pencil className="w-4 h-4 text-[#5C6B64]" /></button>
                      <button onClick={() => onDelete(p.id)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
